/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, {
  ChangeEvent,
  createRef,
  forwardRef,
  MouseEvent,
  Ref,
  RefAttributes,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import debounce from "lodash.debounce";
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from "react-redux";
import { useHistory, useParams } from 'react-router-dom';
import { formatDistanceStrict } from 'date-fns';

import {
    Avatar,
    Button,
    Container,
    Grid,
    IconButton,
    TableCell,
    TableRow,
    TextField,
    Typography,
    CssBaseline,
    Chip,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { AddRounded, ArrowDropDown, ChevronLeft, ChevronRight } from "@material-ui/icons";
import classNames from "classnames";
import MaterialTable, { Column, MTableBodyRow } from "@material-table/core";

import {
    FIDP, RETURN_TO_PATH, RESOURCE_LOGIN, RESOURCE_SIGN_IN,
    SELECTED_ORG_UUID, ORG_QUERY_PARAM, PATH_SEPARATOR
} from "../../../data/constants";
import {
    Application, ApplicationReducer, ApplicationState, NotificationReducer, StorageOperations, UserReducer
} from "../../../data/interfaces";
import {
    deleteApplication, fetchApplicationList, resetNotificationMsg, searchApplications
} from "../../../data/operations";
import {
    isSelfSignupEnabled, localStoreOperation, sessionStoreOperation
} from "../../../data/utils";
import { PreLoaderLogo } from "../../../elements/PreLoaderLogo";
import { PrimaryButtonRounded } from "../../../elements/PrimaryButtonRounded";
import { Status } from "../../../elements/Status";
import CreateApp from '../../app/CreateApp';
import { ConfirmationDialog } from "../../shared/ConfirmationDialog/ConfirmationDialog";
import DevPortalFooter from "../../shared/DevPortalFooter";
import AppLandingContent from "../AppLandingPage/AppLandingPage";

import "./style.scss";
import { useStyles } from './styles';
import { useSnackbar } from "notistack";
import NoPermission from '../../shared/NoPermission';
import {
    trackApplicationSelection,
    trackApplicationSelectionDelete,
    trackApplicationSelectionEdit
} from "../../../util/app-insights";
import ShareIcon from '../../../icons/Share';
import ShareApplication from '../../app/ShareApplication';
import { AppPermission } from '../../../data/emus';
import { ApplicationPermission } from '../../../util/app-permissions';

interface AppListProps {
    userReducer: UserReducer,
    applicationReducer: ApplicationReducer,
    notificationReducer: NotificationReducer,
    resetNotificationMessage: () => void,
    dispatchFetchAppList: () => void,
    dispatchDeleteApplication: (applicationId: string) => void
}

interface RowData {
    id: string,
    name: string,
    isOwner: boolean,
    subscriptionCount?: number,
    createdTime?: string | JSX.Element,
    isPending?: boolean
}

interface RowProps {
    data: RowData;
}

interface SortingData {
    columnName: string,
    asc: boolean
}

const defaultSortedColumn: SortingData = { columnName: 'name', asc: true };
const SEARCH_DEBOUNCE_INTERVAL = 700; // ms
const MIN_BACKEND_SEARCH_LENGTH = 5;
const COLUMN_WIDTH = {
    application: "40%",
    status: "20%",
    subscriptions: "5%",
    created: "15%"
};
const SEARCH_PENDING_ROW_ID = "__search_pending__";

function AppListC(props: AppListProps) {
    const classes = useStyles();
    const intl = useIntl();
    const history = useHistory();
    const { orgName } = useParams<{orgName: string}>();
    const { enqueueSnackbar } = useSnackbar();
    const {
        userReducer, applicationReducer, dispatchFetchAppList, dispatchDeleteApplication, notificationReducer,
        resetNotificationMessage
    } = props;
    const { type: notificationType, message: notification } = notificationReducer;
    const { apimTokenObtained, orgs: userOrganizations, selectedOrg, userInfo, user } = userReducer;
    const scopes = user.allowedScopes
    const apps = applicationReducer.applications?.list;
    const isLoading = applicationReducer.isLoading;
    const selectedOrgHandleName = selectedOrg.handle;
    const [sortedColumn, setSortedColumn] = useState(defaultSortedColumn);
    const [isDeleteAppWizardOpen, setDeleteAppWizardOpen] = useState(false);
    const [isSelected, setIsSelected] = useState("");
    const [selectedApp, setSelectedApp] = useState<Application>();
    const [appDeletionError, setAppDeletionError] = useState("");
    const [list, setListData] = useState<RowData[]>([]);
    const [tableColumns, setTableColumns] = useState<Column<RowData>[]>();
    const [isNewAppWizardOpen, setIsNewAppWizardOpen] = useState(false);
    const [isAppSharingOpen, setAppSharingOpen] = useState(false);
    const [isShown, setIsShown] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [pageNumber, setPageNumber] = useState(0);
    const [matchedApplicationIds, setMatchedApplicationIds] = useState<string[]>([]);
    const [isSearchPending, setSearchPending] = useState(false);
    // Identifies the most recent search call, so that a slower earlier response cannot overwrite a later one.
    const latestSearchId = useRef(0);
    const appTable = createRef<HTMLDivElement>();

    const checkUserAccessToCurrentPathOrg = (): boolean => {
        const selectedOrganization = userOrganizations.find(userOrg => userOrg.handle === orgName);
        return !!selectedOrganization;
    }

    const hasUserAccessToCurrentPathOrg = checkUserAccessToCurrentPathOrg();

    useEffect(() => {
        if (notification !== "") {
            enqueueSnackbar(notification, { variant: notificationType });
            resetNotificationMessage();
        }
    }, [notificationReducer]);

    useEffect(() => {
        if (!apimTokenObtained) {
            sessionStoreOperation(StorageOperations.ADD, RETURN_TO_PATH, window.location.pathname);
            const queryParam = new URLSearchParams(location.search);
            const orgUuid = queryParam.get(ORG_QUERY_PARAM);
            if (orgUuid) {
                localStoreOperation(StorageOperations.ADD, SELECTED_ORG_UUID, orgUuid);
            }
            // Checking whether request is from choreo portal and setting fidp value
            if (queryParam.get('fidp')) {
                localStoreOperation(StorageOperations.ADD, FIDP, queryParam.get('fidp') ?? "");
                history.push(RESOURCE_SIGN_IN);
            } else if (localStoreOperation(StorageOperations.GET, FIDP)) {
                // Redirecting user to sigin if fidp value already exists in local store
                history.push(RESOURCE_SIGN_IN);
            } else if (!isSelfSignupEnabled(orgName)) {
                // Redirecting user to login page if fidp value does not exist
                // and selected org is not a self sign up enabled org
                history.push(RESOURCE_LOGIN);
            } else {
                // Redirecting user to devportal home page if fidp value does not exist
                // and selcted org have self signup enabled
                history.push(`/${orgName}`);
            }
        } else if (hasUserAccessToCurrentPathOrg) {
            dispatchFetchAppList();
        }
    }, [selectedOrg]);

    const toggleHover = (event: MouseEvent<HTMLTableRowElement>, rowData: RowProps) => {
        event.stopPropagation();
        setIsSelected(rowData.data.name);
    };

    const mouseLeaveMoreButton = (event: MouseEvent<HTMLTableRowElement>) => {
        event.stopPropagation();
        setIsSelected("");
    };
    
    const searchBackend = useMemo(() => debounce((searchText: string, searchId: number) => {
        searchApplications(searchText)
            .then((response: { data: { list: Application[] } }) => {
                if (searchId !== latestSearchId.current) {
                    return;
                }
                setMatchedApplicationIds(response?.data?.list?.map((app) => app.applicationId as string) ?? []);
                setSearchPending(false);
            })
            .catch(() => {
                if (searchId !== latestSearchId.current) {
                    return;
                }
                setMatchedApplicationIds([]);
                setSearchPending(false);
            });
    }, SEARCH_DEBOUNCE_INTERVAL), []);

    // Cancel pending search calls on unmount
    useEffect(() => () => searchBackend.cancel(), [searchBackend]);

    const handleSearchFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
        const searchText = event.target.value;
        setSearchQuery(searchText);
        const trimmedSearchText = searchText.trim();
        latestSearchId.current += 1;
        setMatchedApplicationIds([]);
        if (trimmedSearchText.length < MIN_BACKEND_SEARCH_LENGTH) {
            searchBackend.cancel();
            setSearchPending(false);
            return;
        }
        setSearchPending(true);
        searchBackend(trimmedSearchText, latestSearchId.current);
    };

    const openDeleteAppWizard = (app: Application) => (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        trackApplicationSelectionDelete(`${app?.applicationId}`);
        setSelectedApp(app);
        setDeleteAppWizardOpen(true);
    };

    const closeDeleteAppWizard = () => {
        setDeleteAppWizardOpen(false);
    };

    const searchButtonPlaceholder = intl.formatMessage({
        id: 'application.home.appList.searchButton.placeholder',
        defaultMessage: 'Search'
    });

    const createButtonLabel = intl.formatMessage({
        id: 'application.home.appList.createButton.label',
        defaultMessage: 'Create'
    });

    const countSuffixSingular = intl.formatMessage({
        id: 'application.home.appList.count.singularSuffix',
        defaultMessage: 'Application'
    });

    const countSuffixPlural = intl.formatMessage({
        id: 'application.home.appList.count.pluralSuffix',
        defaultMessage: 'Applications'
    });

    const appTableI18n = {
        application: intl.formatMessage({
            id: 'application.home.appList.table.column.application',
            defaultMessage: 'APPLICATION'
        }),
        status: intl.formatMessage({
            id: 'application.home.appList.table.column.status',
            defaultMessage: 'STATUS'
        }),
        subscriptions: intl.formatMessage({
            id: 'application.home.appList.table.column.subscriptions',
            defaultMessage: 'SUBSCRIPTIONS'
        }),
        created: intl.formatMessage({
            id: 'application.home.appList.table.column.created',
            defaultMessage: 'CREATED'
        }),
        nextPageLabel: intl.formatMessage({
            id: 'application.home.appList.table.nextPageLabel',
            defaultMessage: 'Next'
        }),
        previousPageLabel: intl.formatMessage({
            id: 'application.home.appList.table.previousPageLabel',
            defaultMessage: 'Previous'
        })
    };

    const confirmationDialogI18n = {
        title: intl.formatMessage({
            id: 'application.home.appList.deleteConfirmationDialog.title',
            defaultMessage: 'Delete Application'
        }),
        content: intl.formatMessage({
            id: 'application.home.appList.deleteConfirmationDialog.content',
            defaultMessage: 'Are you sure you want to delete application - ' + selectedApp?.name + ' ?'
        }),
        okayButtonLabel: intl.formatMessage({
            id: 'application.home.appList.deleteConfirmationDialog.okButtonLabel',
            defaultMessage: 'Delete'
        }),
        cancelButtonLabel: intl.formatMessage({
            id: 'application.home.appList.deleteConfirmationDialog.cancelButtonLabel',
            defaultMessage: 'Cancel'
        }),
        loadingText: intl.formatMessage({
            id: 'application.home.appList.deleteConfirmationDialog.loadingText',
            defaultMessage: 'Deleting...'
        })
    };

    const DeleteBtn = (app: Application) => {
        return (
            <Button
                disableRipple={true}
                disableTouchRipple={true}
                aria-label="delete"
                onClick={openDeleteAppWizard(app)}
                data-testid={`delete-btn-${app.name}`}
                className={classes.deleteBtn}
            >
                <Typography className={classes.btnText}>
                    <FormattedMessage id='application.home.appList.delete' defaultMessage='Delete' />
                </Typography>
            </Button>
        );
    };

    const MoreOptions = (btnProps: { app: Application }) => {
        const { app } = btnProps;
        return (
            <div className={classes.moreButtons}>
                {/* TODO - will add share functionality by a separate PR */}
                {/* { ApplicationPermission.hasPermission(AppPermission.SHARE, app, userInfo) && ShareBtn(app) } */}
                { ApplicationPermission.hasPermission(AppPermission.WRITE, app, userInfo, scopes) && EditBtn(app) }
                { ApplicationPermission.hasPermission(AppPermission.WRITE, app, userInfo, scopes) && DeleteBtn(app) }
            </div>
        );
    };

    const mapAppToRow = () => {
        setListData([]);
        apps?.forEach((app: Application) => {
            const appStatus: JSX.Element = 
                <Status type={app.status?.toLowerCase()} alignCenter={true}/>;
            setListData((prevState: RowData[]) => (
                [...prevState, {
                    id: app.applicationId as string,
                    name: app.name,
                    isOwner: app.owner === userInfo.sub,
                    status: appStatus,
                    subscriptionCount: app.subscriptionCount,
                    createdTime: formatDistanceStrict(new Date(parseInt(app.createdTime)), new Date(), { addSuffix: true, })
                }]
            ));
        });
    };

    useEffect(() => {
        const modified = list?.map((tableItem: RowData) => {
            const matchingApp = apps?.find(app => app.name === tableItem.name);
            const createdTime = formatDistanceStrict(new Date(parseInt(matchingApp?.createdTime || '')),
            new Date(),
            { addSuffix: true, });
            return {
                ...tableItem,
                createdTime: isSelected === tableItem.name ? <MoreOptions app={matchingApp as Application} /> :
                createdTime
            };
        });
        setListData(modified);
    }, [isSelected]);

    const getSortingOrder = (columnName: string): boolean => {
        return sortedColumn.columnName === columnName ? !sortedColumn.asc : true;
    };

    const handleCustomSort = (column: number) => {
        switch (column) {
            case 0:
                setSortedColumn({ columnName: 'name', asc: getSortingOrder('name') });
                break;
            case 1:
                setSortedColumn({ columnName: 'status', asc: getSortingOrder('status') });
                break;
            case 2:
                setSortedColumn({ columnName: 'subscriptionCount', asc: getSortingOrder('subscriptionCount') });
                break;
            case 3:
                setSortedColumn({ columnName: 'createdTime', asc: getSortingOrder('createdTime') });
                break;
            default:
                break;
        }
    };

    const RenderApplicationColumn = (rowData: RowData) => {
        return (
            <div>
                <Avatar classes={{ colorDefault: classes.defaultThumbnail }}>
                    {rowData.name.substr(0, 1).toUpperCase()}
                </Avatar>
                <Typography className={classes.cellContent}> {rowData.name} </Typography>
                {
                    rowData.isOwner && <Chip size="small" label="owner" className={classes.ownerChip} />
                }
            </div>
        );
    };

    const columns = useMemo(() => {
        return [{
            title: appTableI18n.application, field: "name", width: COLUMN_WIDTH.application,
            defaultSort: sortedColumn.columnName === 'name' ? (sortedColumn.asc ? 'asc' : 'desc') : undefined,
            cellStyle: { fontSize: "1rem" },
            // The pending row is appended, not one of the real results, so it always sorts last here.
            // Note : This guarantees only when sorted by name.
            customSort: (a: RowData, b: RowData) => {
                if (a.isPending || b.isPending) {
                    return a.isPending ? 1 : -1;
                }
                return a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());
            },
            render: RenderApplicationColumn
        },
        {
            title: appTableI18n.status, field: "status", width: COLUMN_WIDTH.status, searchable: false, sorting: true,
            defaultSort: sortedColumn.columnName === 'status' ? (sortedColumn.asc ? 'asc' : 'desc') : undefined,
            align: "center",
            cellStyle: { fontSize: 12 }
        },
        {
            title: appTableI18n.subscriptions, field: "subscriptionCount", width: COLUMN_WIDTH.subscriptions,
            searchable: false, align: "center",
            defaultSort: sortedColumn.columnName === 'subscriptionCount' ? (sortedColumn.asc ? 'asc' : 'desc') : undefined
        },
        {
            title: appTableI18n.created, field: "createdTime", width: COLUMN_WIDTH.created, searchable: false,
            align: "right",
            defaultSort: sortedColumn.columnName === 'created' ? (sortedColumn.asc ? 'asc' : 'desc') : undefined
        }];
    }, [sortedColumn]);

    useEffect(() => {
        setTableColumns(columns as Column<RowData>[]);
        mapAppToRow();
    }, [columns, apps]);

    useLayoutEffect(() => {
        const updateSize = async () => {
            setRowsPerPage(Math.min(Math.max(Math.floor((window.innerHeight - 484) / 56), 3), 15))
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    const handlePageNumberChange = (page: number) => {
        setPageNumber(page * rowsPerPage / list.length)
    };

    const handleAppOpen = (appId: string) => {
        history.push(PATH_SEPARATOR + selectedOrgHandleName + '/applications/' + appId + '/overview');
    };

    const openNewAppWizard = () => {
        setIsEdit(false);
        setIsNewAppWizardOpen(true);
    };

    const openEditAppWizard = (app: Application) => (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        trackApplicationSelectionEdit(`${app?.applicationId}`);
        setSelectedApp(app);
        setIsEdit(true);
        setIsNewAppWizardOpen(true);
    };
    
    const openShareAppWizard = (app: Application) => (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setSelectedApp(app);
        setAppSharingOpen(true);
    };

    const closeNewAppWizard = () => {
        setIsNewAppWizardOpen(false);
    };

    const onCloseAppSharing = () => {
        setAppSharingOpen(false);
    };

    const triggerShow = () => {
        if (!isShown || (isShown && searchQuery.length === 0)) {
            setIsShown(!isShown);
        }
    };

    const onDeleteDialogOkClick = async () => {
        setAppDeletionError('');
        await dispatchDeleteApplication(selectedApp?.applicationId as string);
        history.push(PATH_SEPARATOR + selectedOrgHandleName + '/applications');
        setDeleteAppWizardOpen(false);
    };

    const getNextPageIcon = (props: RefAttributes<SVGSVGElement>,
        ref: Ref<SVGSVGElement>) => <ChevronRight {...props} ref={ref} />;

    const getPreviousPageIcon = (props: RefAttributes<SVGSVGElement>,
        ref: Ref<SVGSVGElement>) => <ChevronLeft {...props} ref={ref} />;

    const getArrowIcon = (props: RefAttributes<SVGSVGElement>,
        ref: Ref<SVGSVGElement>) => <ArrowDropDown{...props} ref={ref} />;

    const CreateBtn = () => {
        return (
            <PrimaryButtonRounded
                dataTestId="create-application-btn"
                onClick={openNewAppWizard}
                startIcon={<AddRounded />}
                text={createButtonLabel}
                className={classes.createBtn}
            />
        );
    };

    const EditBtn = (app: Application) => {
        return (
            <Button
                disableRipple={true}
                disableTouchRipple={true}
                aria-label="Edit"
                onClick={openEditAppWizard(app)}
                className={classNames(classes.hoverBtn, classes.editBtn)}
                data-testid={`edit-btn-${app.name}`}
            >
                <Typography className={classes.btnText}>
                    <FormattedMessage
                        id='application.home.appList.editButton.label'
                        defaultMessage='Edit'
                    />
                </Typography>
            </Button>
        );
    };

    const ShareBtn = (app: Application) => {
        return (
            <Button
                disableRipple={true}
                disableTouchRipple={true}
                aria-label="Share"
                onClick={openShareAppWizard(app)}
                className={classNames(classes.hoverBtn, classes.shareBtn)}
                startIcon={<ShareIcon />}
                data-testid={`share-btn-${app.name}`}
            >
                <Typography className={classes.btnText}>
                    <FormattedMessage
                        id='application.home.appList.shareButton.label'
                        defaultMessage='Share'
                    />
                </Typography>
            </Button>
        );
    };

    const SearchBtn = () => {
        return (
            <IconButton
                disableRipple={true}
                disableFocusRipple={true}
                onMouseEnter={triggerShow}
                onClick={triggerShow}
                aria-label="search"
                data-testid="search-btn"
                className={classes.searchIcon}
            >
                <img src="../../themes/default/images/search.svg" />
            </IconButton>
        );
    };

    const SearchField = () => {
        return (
            <div className={classes.searchField}>
                <TextField
                    data-testid="search-app"
                    onChange={handleSearchFieldChange}
                    value={searchQuery}
                    onMouseLeave={triggerShow}
                    onBlur={triggerShow}
                    placeholder={searchButtonPlaceholder}
                />
            </div>
        );
    };

    const tableIcons = {
        NextPage: forwardRef(getNextPageIcon),
        PreviousPage: forwardRef(getPreviousPageIcon),
        SortArrow: forwardRef(getArrowIcon)
    };

    const SearchPendingRow = () => (
        <TableRow hover data-testid="search-pending-skeleton">
            <TableCell style={{ width: COLUMN_WIDTH.application }} className={classes.searchPendingAvatarCell}>
                <Skeleton animation="wave" variant="circle" width={40} height={40} />
                <Skeleton animation="wave" variant="text" width="55%" height={28} className={classes.searchPendingNameSkeleton} />
            </TableCell>
            <TableCell align="center" style={{ width: COLUMN_WIDTH.status }}>
                <Skeleton
                    animation="wave" variant="text" width="60%" height={20}
                    className={classes.searchPendingCenteredSkeleton}
                />
            </TableCell>
            <TableCell align="center" style={{ width: COLUMN_WIDTH.subscriptions }}>
                <Skeleton
                    animation="wave" variant="text" width="80%" height={20}
                    className={classes.searchPendingCenteredSkeleton}
                />
            </TableCell>
            <TableCell align="right" style={{ width: COLUMN_WIDTH.created }}>
                <Skeleton
                    animation="wave" variant="text" width="70%" height={20}
                    className={classes.searchPendingRightAlignedSkeleton}
                />
            </TableCell>
        </TableRow>
    );

    const MaterialRow = (rowProps: RowProps) => {
        if (rowProps.data.isPending) {
            return <SearchPendingRow />;
        }
        return (
            <MTableBodyRow
                onMouseEnter={(event: MouseEvent<HTMLTableRowElement>) => { toggleHover(event, rowProps) }}
                onMouseLeave={(event: MouseEvent<HTMLTableRowElement>) => { mouseLeaveMoreButton(event) }}
                data-testid={`application-list-${rowProps.data.name}`}
                {...rowProps}
            />
        );
    };

    // Filtering is done here rather than through the table's own search, so that an application the
    // backend matched on something other than its name is not filtered back out for not containing
    // the search term.
    const displayedRows = useMemo(() => {
        const trimmedQuery = searchQuery.trim();
        if (!trimmedQuery) {
            return list;
        }
        const searchText = trimmedQuery.toLowerCase();
        return list?.filter((row: RowData) => row.name.toLowerCase().includes(searchText)
            || matchedApplicationIds.includes(row.id));
    }, [list, searchQuery, matchedApplicationIds]);

    const tableData = useMemo(() => (
        isSearchPending
            ? [...displayedRows, { id: SEARCH_PENDING_ROW_ID, name: "", isOwner: false, isPending: true }]
            : displayedRows
    ), [displayedRows, isSearchPending]);

    const AppsTable = () => {
        return (
            <div style={{ maxWidth: "100%" }} data-testid={"app-table"} key={searchQuery} className={classes.appTable}>
                <MaterialTable
                    onOrderChange={handleCustomSort}
                    key={list.toString() + rowsPerPage.toString()}
                    onRowsPerPageChange={(pageSize) => { setRowsPerPage(pageSize) }}
                    onPageChange={handlePageNumberChange}
                    options={{
                        pageSize: 5,
                        initialPage: list.length > 0 ? Math.round(pageNumber * list.length / rowsPerPage) : 0,
                        showFirstLastPageButtons: false,
                        thirdSortClick: false,
                        pageSizeOptions: Array.from(Array(15).keys())
                    }}
                    columns={tableColumns as Column<RowData>[]}
                    icons={tableIcons}
                    data={tableData}
                    onRowClick={(_, rowData) => {
                        if (rowData?.isPending) {
                            return;
                        }
                        handleAppOpen(rowData?.id as string);
                        trackApplicationSelection(`${rowData?.id}`);
                    }}
                    localization={{
                        pagination: {
                            nextTooltip: appTableI18n.nextPageLabel,
                            previousTooltip: appTableI18n.previousPageLabel
                        }
                    }}
                    components={{ Row: MaterialRow }}
                />
                <ConfirmationDialog
                    open={isDeleteAppWizardOpen}
                    dialogTitle={confirmationDialogI18n.title}
                    dialogContentText={confirmationDialogI18n.content}
                    okayButtonLabel={confirmationDialogI18n.okayButtonLabel}
                    cancelButtonLabel={confirmationDialogI18n.cancelButtonLabel}
                    okButtonTestId='delete-dialog-ok-button'
                    cancelButtonTestId='delete-dialog-cancel-button'
                    isActionProcessing={isLoading}
                    error={appDeletionError}
                    loadingActionHelperText={confirmationDialogI18n.loadingText}
                    onClose={closeDeleteAppWizard}
                    onCancel={closeDeleteAppWizard}
                    onOkay={onDeleteDialogOkClick}
                />
            </div>
        );
    };

    const countSuffix = apps?.length > 1 ? countSuffixPlural : countSuffixSingular;

    const AppListContent = () => {
        if (isLoading) {
            return (
                <div className={classes.loaderContainer}>
                    <PreLoaderLogo size="medium" />
                </div>
            );
        } else {
            return (
                <div className={[classes.applicationList, "application-list"].join(" ")}>
                    <Grid container={true} direction="row" justifyContent="center" alignItems="center">
                        <Grid item={true} xs={12} sm={12} md={12}>
                            <div className={classes.titleSection}>
                                <Container fixed={true}>
                                    <div>
                                        <div className={classes.titleContainer}>
                                            <div className={classes.titleApps} role="heading">
                                                <Typography variant="h3" className={classes.title}>
                                                    <FormattedMessage
                                                        id='application.home.appList.countText'
                                                        defaultMessage='You have {appCount} {countSuffix}'
                                                        values={{ appCount: apps?.length, countSuffix: countSuffix }}
                                                    />
                                                </Typography>
                                            </div>
                                            {isShown ? SearchField() : SearchBtn()}
                                            {(!isLoading) ? CreateBtn() : null}
                                        </div>
                                    </div>
                                </Container>
                            </div>
                        </Grid>
                    </Grid>
                    <Grid className={classes.tableArea} container={true} direction="row" justifyContent="center" alignItems="center">
                        <Grid item={true} xs={12} sm={12} md={12}>
                            <Container fixed={true} ref={appTable}>
                                {AppsTable()}
                            </Container>
                        </Grid>
                    </Grid>
                    {isNewAppWizardOpen &&
                        <CreateApp
                            open={isNewAppWizardOpen}
                            onClose={closeNewAppWizard}
                            isEdit={isEdit}
                            application={isEdit ? selectedApp : undefined}
                            redirectToApplication={true}
                        />
                    }
                    { isAppSharingOpen && 
                        <ShareApplication
                            open={isAppSharingOpen}
                            onClose={onCloseAppSharing}
                            application={selectedApp}
                        />
                    }
                </div>
            );
        }
    };

    const Content = () => {
        if (isLoading) {
            return (
                <div className={classes.loaderContainer}>
                    <PreLoaderLogo size="medium" />
                </div>
            );
        } else if (!hasUserAccessToCurrentPathOrg) {
            return (
                <NoPermission />
            );
        } else if (apps && apps.length === 0) {
            return (
                <AppLandingContent />
            );
        } else if (apps && apps?.length > 0) {
            return (
                <div>
                    <Typography className={classes.pageTitle}>
                        <FormattedMessage
                            id='application.home.appList.title'
                            defaultMessage='My Applications'
                        />
                    </Typography>
                    <Typography variant="subtitle1" className={classes.subtitle}>
                        <FormattedMessage
                            id='application.home.appList.subTitle'
                            defaultMessage={'Manage API subscriptions through one of your'
                                + ' applications or create new'}
                        />
                    </Typography>
                    {AppListContent()}
                </div>
            );
        }
    };

    return (
        <div className={classes.root}>
            <CssBaseline />
            <Grid className={classes.content} container={true} direction="row" justifyContent="center" alignItems="center">
                <Grid item={true} xs={12} sm={12} md={12}>
                    <Container fixed={true}>
                        {Content() as JSX.Element}
                    </Container>
                </Grid>
            </Grid>
            <DevPortalFooter />
        </div>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        userReducer: state.userReducer,
        applicationReducer: state.applicationReducer,
        notificationReducer: state.notificationReducer
    }
};

const mapDispatchToProps = {
    dispatchDeleteApplication: deleteApplication,
    dispatchFetchAppList: fetchApplicationList,
    resetNotificationMessage: resetNotificationMsg
};

const AppList = connect(mapStateToProps, mapDispatchToProps)(AppListC);
export default AppList;
